import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import Popup from "../../../components/Popup";
import AxionControls from "../../../components/controls/AxionControls";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { fetchActiveTool } from "../../../fetchers/cmmsToolMonitoring";
import { reportIssue, uploadWorkOrderAttachment } from "../../../fetchers/cmmsWorkOrder";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { WorkOrderType } from "../../cmmsWorkOrders/workOrderEnums";
import cmmsMessages from "../cmmsMessages";
import messages from "../messages";
import { formatCmmsError } from "./CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
  getSemanticButtonSx,
} from "../assetHierarchyStyles";

export default function QuickReportDialog({ open, onClose, assetNodeId, assetName, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const [companyKey] = useLocalStorage("companyKey", 1);
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  const [workOrderType, setWorkOrderType] = useState(WorkOrderType.MachineBreakdown);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Advisory only — the server resolves the tool again when the issue is reported, so this is
  // never sent back and a failure here must not affect the report. No error state on purpose:
  // there is nothing the operator could do about it, and an alarm about a line that is blank
  // most of the time would be noise.
  const activeToolQuery = useQuery({
    queryKey: ["cmmsActiveTool", companyKey, assetNodeId],
    queryFn: () => fetchActiveTool(companyKey, assetNodeId),
    enabled: open && Boolean(assetNodeId),
  });

  const activeTool = activeToolQuery.data;

  useEffect(() => {
    if (!open) return;
    setWorkOrderType(WorkOrderType.MachineBreakdown);
    setTitle(assetName ? intl.formatMessage(messages.reportDefaultTitle, { asset: assetName }) : "");
    setDescription("");
    setFile(null);
    setErrors({});
  }, [open, assetName, intl]);

  const validate = () => {
    const next = {};
    if (!title.trim()) next.title = intl.formatMessage(messages.reportTitleRequired);
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFileChange = (event) => {
    setFile(event.target.files?.[0] ?? null);
  };

  const clearFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!assetNodeId) {
      pushErrorMessage(intl.formatMessage(messages.reportNoAsset));
      return;
    }
    if (!validate()) return;

    setSubmitting(true);
    try {
      const created = await reportIssue(assetNodeId, {
        companyKey,
        title: title.trim(),
        description: description.trim() || null,
        workOrderType,
      });

      // Photo (optional) is uploaded as an attachment on the freshly created work order.
      if (file && created?.id) {
        try {
          await uploadWorkOrderAttachment(created.id, {
            companyKey,
            file,
          });
        } catch (uploadError) {
          pushErrorMessage(intl.formatMessage(messages.reportPhotoFailed, {
            error: formatCmmsError(uploadError, intl.formatMessage(messages.reportFailed)),
          }));
        }
      }

      pushNotification(intl.formatMessage(messages.reportSuccess));
      await queryClient.invalidateQueries({ queryKey: ["cmmsWorkOrders"] });
      // The reporter may sit in the department the new work order lands in, in which case
      // it counts towards their own badge. The SignalR event covers everyone else.
      await queryClient.invalidateQueries({ queryKey: ["cmmsMyWorkOrders"] });
      onSuccess?.(created);
      onClose?.();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.reportFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(messages.reportDialogTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        {assetName &&
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.reportAssetLabel} />: <strong>{assetName}</strong>
          </Typography>
        }

        {/*
          * Rendered only when something is mounted. A machine standing without a production
          * order reports the "#NA" sentinel, which the server resolves to nothing — and a line
          * saying "no tool" on every report at a site that does not track tools would be noise.
          *
          * The unmatched case is the reason this line exists at all: "the production order
          * names a tool with no row in the register" is invisible everywhere else in the
          * system, and the operator standing at the machine is the cheapest detector for it.
          * Ambiguous matches render identically — to the operator both mean "we cannot name a
          * row for this"; only the server log tells them apart.
          */}
        {activeTool?.toolName &&
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            <FormattedMessage
              {...(activeTool.toolId ? messages.reportActiveTool : messages.reportActiveToolUnknown)}
              values={{ tool: <strong>{activeTool.toolName}</strong> }}
            />
          </Typography>
        }

        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
            <FormattedMessage {...messages.fieldType} />
          </Typography>
          <ToggleButtonGroup
            exclusive
            value={workOrderType}
            onChange={(_event, value) => { if (value != null) setWorkOrderType(value); }}
            size="small"
          >
            <ToggleButton
              value={WorkOrderType.MachineBreakdown}
              sx={{ "&.Mui-selected": { backgroundColor: "#d32f2f", color: "#fff", "&:hover": { backgroundColor: "#b71c1c" } } }}
            >
              <FormattedMessage {...messages.reportTypeStoppage} />
            </ToggleButton>
            <ToggleButton
              value={WorkOrderType.MachineProblem}
              sx={{ "&.Mui-selected": { backgroundColor: "#ed6c02", color: "#fff", "&:hover": { backgroundColor: "#c75c00" } } }}
            >
              <FormattedMessage {...messages.reportTypeProblem} />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          label={intl.formatMessage(messages.fieldTitle)}
          required
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          error={Boolean(errors.title)}
          helperText={errors.title}
          size="small"
          fullWidth
        />

        <TextField
          label={intl.formatMessage(messages.fieldDescription)}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          multiline
          rows={4}
          size="small"
          fullWidth
        />

        <Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Button
              variant="outlined"
              startIcon={<PhotoCameraIcon />}
              onClick={() => fileInputRef.current?.click()}
              sx={getOutlinedButtonSx(cmmsColors)}
            >
              <FormattedMessage {...messages.reportAddPhoto} />
            </Button>
            {file &&
              <>
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>{file.name}</Typography>
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={clearFile} sx={getSemanticButtonSx()}>
                  <FormattedMessage {...messages.reportRemovePhoto} />
                </Button>
              </>
            }
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...messages.reportSubmit} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
